import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Trash2, 
  FileText, 
  ArrowLeft,
  Edit2,
  Save,
  X,
  FolderOpen,
  Search,
  Calendar,
  Link2,
  Building2,
  ChevronDown
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { BUSINESS_TYPES } from '../constants/businessTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationTemplates = ({ user }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDeadline, setNewTemplateDeadline] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  
  // Document search states
  const [searchResults, setSearchResults] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [customDocName, setCustomDocName] = useState('');
  const [showAddDocDialog, setShowAddDocDialog] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [user]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/application-templates?user_id=${user.user_id}`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης προτύπων');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Εισάγετε όνομα προτύπου');
      return;
    }
    try {
      const response = await axios.post(`${API}/application-templates?user_id=${user.user_id}`, {
        name: newTemplateName,
        documents: [],
        deadline: newTemplateDeadline || null
      });
      setTemplates([...templates, response.data]);
      setNewTemplateName('');
      setNewTemplateDeadline('');
      setShowNewTemplateDialog(false);
      setSelectedTemplate(response.data);
      toast.success('Το πρότυπο δημιουργήθηκε');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας προτύπου');
    }
  };

  const handleUpdateDeadline = async () => {
    if (!selectedTemplate) return;
    try {
      // Always sync deadline to all linked projects
      const response = await axios.put(
        `${API}/application-templates/${selectedTemplate.template_id}`,
        { deadline: editDeadline || null }
      );
      setSelectedTemplate(response.data);
      setTemplates(templates.map(t => 
        t.template_id === response.data.template_id ? response.data : t
      ));
      setShowDeadlineDialog(false);
      const projectsUpdated = response.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0 
          ? `Η προθεσμία ενημερώθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Η προθεσμία ενημερώθηκε'
      );
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης προθεσμίας');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το πρότυπο;')) return;
    try {
      await axios.delete(`${API}/application-templates/${templateId}`);
      setTemplates(templates.filter(t => t.template_id !== templateId));
      if (selectedTemplate?.template_id === templateId) {
        setSelectedTemplate(null);
      }
      toast.success('Το πρότυπο διαγράφηκε');
    } catch (error) {
      toast.error('Σφάλμα διαγραφής προτύπου');
    }
  };

  // Search document templates from the predefined list
  const handleSearchTemplates = async (query) => {
    setNewDocumentName(query);
    if (query.length === 0) {
      // Fetch initial templates when empty
      try {
        const response = await axios.get(`${API}/document-templates/search?q=`);
        setSearchResults(response.data);
      } catch (error) {
        setSearchResults([]);
      }
    } else if (query.length >= 1) {
      try {
        const response = await axios.get(`${API}/document-templates/search?q=${encodeURIComponent(query)}`);
        setSearchResults(response.data);
      } catch (error) {
        setSearchResults([]);
      }
    }
  };

  // Add document from predefined list
  const handleAddDocumentFromList = async (documentName) => {
    if (!documentName.trim() || !selectedTemplate) return;
    try {
      // Always sync to linked projects
      const addResponse = await axios.post(`${API}/application-templates/${selectedTemplate.template_id}/documents?sync_projects=true`, {
        document_name: documentName
      });
      // Refresh template
      const response = await axios.get(`${API}/application-templates/${selectedTemplate.template_id}`);
      setSelectedTemplate(response.data);
      setTemplates(templates.map(t => t.template_id === response.data.template_id ? response.data : t));
      setNewDocumentName('');
      setSearchResults([]);
      setShowAddDocDialog(false);
      
      const projectsUpdated = addResponse.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Το δικαιολογητικό προστέθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Το δικαιολογητικό προστέθηκε'
      );
    } catch (error) {
      toast.error('Σφάλμα προσθήκης δικαιολογητικού');
    }
  };

  // Add custom document
  const handleAddCustomDocument = async (e) => {
    e.preventDefault();
    if (!customDocName.trim() || !selectedTemplate) return;
    try {
      // Always sync to linked projects
      const addResponse = await axios.post(`${API}/application-templates/${selectedTemplate.template_id}/documents?sync_projects=true`, {
        document_name: customDocName
      });
      // Refresh template
      const response = await axios.get(`${API}/application-templates/${selectedTemplate.template_id}`);
      setSelectedTemplate(response.data);
      setTemplates(templates.map(t => t.template_id === response.data.template_id ? response.data : t));
      setCustomDocName('');
      setShowOtherInput(false);
      setShowAddDocDialog(false);
      
      const projectsUpdated = addResponse.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Το δικαιολογητικό προστέθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Το δικαιολογητικό προστέθηκε'
      );
    } catch (error) {
      toast.error('Σφάλμα προσθήκης δικαιολογητικού');
    }
  };

  const handleRemoveDocument = async (docIndex) => {
    if (!selectedTemplate) return;
    try {
      await axios.delete(`${API}/application-templates/${selectedTemplate.template_id}/documents/${docIndex}`);
      // Refresh template
      const response = await axios.get(`${API}/application-templates/${selectedTemplate.template_id}`);
      setSelectedTemplate(response.data);
      setTemplates(templates.map(t => t.template_id === response.data.template_id ? response.data : t));
      toast.success('Το δικαιολογητικό αφαιρέθηκε');
    } catch (error) {
      toast.error('Σφάλμα αφαίρεσης δικαιολογητικού');
    }
  };

  const handleToggleBusinessType = async (docIndex, businessType) => {
    if (!selectedTemplate) return;
    const docs = [...selectedTemplate.documents];
    const doc = { ...docs[docIndex] };
    const current = doc.business_types || [];
    if (current.includes(businessType)) {
      doc.business_types = current.filter(bt => bt !== businessType);
    } else {
      doc.business_types = [...current, businessType];
    }
    docs[docIndex] = doc;
    try {
      await axios.put(`${API}/application-templates/${selectedTemplate.template_id}`, { documents: docs });
      const response = await axios.get(`${API}/application-templates/${selectedTemplate.template_id}`);
      setSelectedTemplate(response.data);
      setTemplates(templates.map(t => t.template_id === response.data.template_id ? response.data : t));
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleUpdateTemplateName = async () => {
    if (!editName.trim() || !editingTemplate) return;
    try {
      await axios.put(`${API}/application-templates/${editingTemplate}`, {
        name: editName
      });
      setTemplates(templates.map(t => t.template_id === editingTemplate ? {...t, name: editName} : t));
      if (selectedTemplate?.template_id === editingTemplate) {
        setSelectedTemplate({...selectedTemplate, name: editName});
      }
      setEditingTemplate(null);
      setEditName('');
      toast.success('Το όνομα ενημερώθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Open add document dialog and fetch initial templates
  const openAddDocumentDialog = () => {
    setShowAddDocDialog(true);
    setShowOtherInput(false);
    setNewDocumentName('');
    setCustomDocName('');
    // Fetch initial list
    handleSearchTemplates('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/select-module')}
              className="text-slate-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Πρότυπα Αιτήσεων</h1>
              <p className="text-sm text-slate-500">Δημιουργήστε πρότυπα με δικαιολογητικά για γρήγορη δημιουργία αιτήσεων</p>
            </div>
          </div>
          <Button
            onClick={() => setShowNewTemplateDialog(true)}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Νέο Πρότυπο
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Τα Πρότυπά μου</h2>
              
              {templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Δεν υπάρχουν πρότυπα</p>
                  <p className="text-sm">Δημιουργήστε το πρώτο σας πρότυπο</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(template => (
                    <div
                      key={template.template_id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTemplate?.template_id === template.template_id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-center justify-between">
                        {editingTemplate === template.template_id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="h-8 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleUpdateTemplateName(); }}
                              className="h-8 w-8 p-0 text-green-600"
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setEditingTemplate(null); }}
                              className="h-8 w-8 p-0 text-slate-400"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div>
                              <div className="font-medium text-slate-900">{template.name}</div>
                              <div className="text-xs text-slate-500">
                                {template.document_count || template.documents?.length || 0} δικαιολογητικά
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTemplate(template.template_id);
                                  setEditName(template.name);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(template.template_id);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Template Details */}
          <div className="lg:col-span-2">
            {selectedTemplate ? (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {selectedTemplate.name}
                  </h2>
                  <span className="text-sm text-slate-500">
                    {selectedTemplate.documents?.length || 0} δικαιολογητικά
                  </span>
                </div>

                {/* Deadline & Connected Projects Info */}
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-600">Προθεσμία:</span>
                    {selectedTemplate.deadline ? (
                      <span className="text-sm font-medium text-slate-800">
                        {new Date(selectedTemplate.deadline).toLocaleDateString('el-GR')}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">Δεν έχει οριστεί</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditDeadline(selectedTemplate.deadline || '');
                        setShowDeadlineDialog(true);
                      }}
                      className="h-7 text-cyan-600 hover:text-cyan-700"
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Αλλαγή
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-600">Συνδεδεμένα έργα:</span>
                    <span className="text-sm font-medium text-slate-800">
                      {selectedTemplate.connected_projects_count || 0}
                    </span>
                  </div>
                </div>

                {/* Add Document Button */}
                <div className="mb-6">
                  <Button 
                    onClick={openAddDocumentDialog} 
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Προσθήκη Δικαιολογητικού
                  </Button>
                </div>

                {/* Documents List */}
                {selectedTemplate.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTemplate.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-cyan-600" />
                            <span className="text-sm font-medium text-slate-700">
                              {doc.document_name}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveDocument(index)}
                            className="text-slate-400 hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {/* Business Types Multi-select */}
                        <div className="mt-2 ml-8">
                          <details className="group">
                            <summary className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                              <Building2 className="h-3 w-3" />
                              {doc.business_types?.length > 0
                                ? <span className="text-cyan-700">{doc.business_types.length} μορφές επιλεγμένες</span>
                                : <span>Όλες οι μορφές επιχείρησης</span>
                              }
                              <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                            </summary>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {BUSINESS_TYPES.map(bt => {
                                const isSelected = (doc.business_types || []).includes(bt);
                                return (
                                  <button
                                    key={bt}
                                    onClick={() => handleToggleBusinessType(index, bt)}
                                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                                      isSelected
                                        ? 'bg-cyan-100 border-cyan-300 text-cyan-800'
                                        : 'bg-white border-slate-200 text-slate-500 hover:border-cyan-200 hover:text-cyan-700'
                                    }`}
                                  >
                                    {bt}
                                  </button>
                                );
                              })}
                            </div>
                          </details>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Δεν υπάρχουν δικαιολογητικά</p>
                    <p className="text-sm">Προσθέστε δικαιολογητικά στο πρότυπο</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-slate-500">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Επιλέξτε ένα πρότυπο</h3>
                <p className="text-sm">ή δημιουργήστε ένα νέο για να ξεκινήσετε</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Template Dialog */}
      <Dialog open={showNewTemplateDialog} onOpenChange={setShowNewTemplateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Νέο Πρότυπο Αίτησης</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Όνομα Προτύπου</Label>
              <Input
                placeholder="π.χ. ΕΣΠΑ Ψηφιακός Μετασχηματισμός"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Προθεσμία (προαιρετικό)</Label>
              <Input
                type="date"
                value={newTemplateDeadline}
                onChange={(e) => setNewTemplateDeadline(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowNewTemplateDialog(false);
                setNewTemplateName('');
                setNewTemplateDeadline('');
              }}>
                Ακύρωση
              </Button>
              <Button onClick={handleCreateTemplate} className="bg-cyan-600 hover:bg-cyan-700">
                Δημιουργία
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deadline Edit Dialog */}
      <Dialog open={showDeadlineDialog} onOpenChange={(open) => {
        setShowDeadlineDialog(open);
        if (!open) {
          setEditDeadline('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ορισμός Προθεσμίας</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Προθεσμία</Label>
              <Input
                type="date"
                value={editDeadline}
                onChange={(e) => setEditDeadline(e.target.value)}
              />
            </div>
            {selectedTemplate?.connected_projects_count > 0 && (
              <div className="flex items-center gap-2 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                <Link2 className="h-4 w-4 text-cyan-600" />
                <span className="text-sm text-cyan-800">
                  Η προθεσμία θα ενημερωθεί αυτόματα σε <strong>{selectedTemplate.connected_projects_count}</strong> συνδεδεμένα έργα
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeadlineDialog(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handleUpdateDeadline} className="bg-cyan-600 hover:bg-cyan-700">
                Αποθήκευση
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showAddDocDialog} onOpenChange={setShowAddDocDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Προσθήκη Δικαιολογητικού</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {!showOtherInput ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="document-search">Αναζήτηση Δικαιολογητικού</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="document-search"
                      value={newDocumentName}
                      onChange={(e) => handleSearchTemplates(e.target.value)}
                      placeholder="Πληκτρολογήστε για αναζήτηση..."
                      className="pl-10"
                      autoComplete="off"
                    />
                  </div>
                </div>
                
                {/* Search Results */}
                <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
                  {searchResults.length > 0 ? (
                    searchResults.map((template) => (
                      <button
                        key={template.template_id}
                        type="button"
                        onClick={() => handleAddDocumentFromList(template.name)}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-cyan-50 hover:text-cyan-700 border-b border-slate-100 last:border-b-0 transition-colors"
                      >
                        <span>{template.name}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-sm text-slate-500 text-center">
                      {newDocumentName ? 'Δεν βρέθηκαν αποτελέσματα' : 'Πληκτρολογήστε για αναζήτηση ή δείτε τη λίστα'}
                    </div>
                  )}
                </div>
                
                {/* Add custom document button */}
                <div className="pt-2 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setShowOtherInput(true)}
                    className="w-full px-4 py-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Άλλο Δικαιολογητικό (custom)
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAddCustomDocument} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="custom-doc-name">Τίτλος Δικαιολογητικού</Label>
                  <Input
                    id="custom-doc-name"
                    value={customDocName}
                    onChange={(e) => setCustomDocName(e.target.value)}
                    placeholder="π.χ. Υπεύθυνη Δήλωση"
                    required
                    autoFocus
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowOtherInput(false);
                      setCustomDocName('');
                    }}
                    className="flex-1"
                  >
                    Πίσω
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  >
                    Προσθήκη
                  </Button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationTemplates;
